import { useState } from "react";
import { Building2, Loader2, Settings2, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { Organization } from "@/types/auth";

export interface OrganizationCardProps {
  org: Organization;
  isCurrent: boolean;
  isSwitching?: boolean;
  onSwitch: (orgId: string) => void;
  onSave: (orgId: string, name: string, description?: string) => void;
}

export function OrganizationCard({ org, isCurrent, isSwitching = false, onSwitch, onSave }: OrganizationCardProps) {
  const canEdit = org.role === "Admin" || org.role === "Owner";
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(org.name);
  const [description, setDescription] = useState(org.description ?? "");

  function openEdit() {
    setName(org.name);
    setDescription(org.description ?? "");
    setEditing(true);
  }

  function handleSave() {
    const trimmed = name.trim();
    if (trimmed.length < 2) return;
    onSave(org.id, trimmed, description.trim());
    setEditing(false);
  }

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-xl border bg-card p-5",
        isCurrent && "border-2 border-primary",
      )}
    >
      {isCurrent && (
        <span className="absolute -top-2.5 left-4 rounded-full bg-primary px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-primary-foreground">
          Current
        </span>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
          <h3 className="truncate text-lg font-semibold">{org.name}</h3>
          {org.isDefault && <Star className="h-4 w-4 shrink-0 fill-primary text-primary" />}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge variant={org.role === "Owner" ? "default" : "secondary"}>{org.role}</Badge>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={openEdit}
              aria-label="Edit organization"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
        {org.description || "No description"}
      </p>

      <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3">
        <span className="text-[11px] text-muted-foreground">
          {isCurrent ? "You're working here" : "Switching moves you to this workspace"}
        </span>
        {!isCurrent && (
          <Button
            variant="outline"
            size="sm"
            disabled={isSwitching}
            onClick={() => onSwitch(org.id)}
          >
            {isSwitching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Switching…
              </>
            ) : (
              "Switch"
            )}
          </Button>
        )}
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit organization</DialogTitle>
            <DialogDescription>Update the workspace name and description.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`org-name-${org.id}`}>Organization name</Label>
              <Input
                id={`org-name-${org.id}`}
                aria-label="Organization name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`org-description-${org.id}`}>Description</Label>
              <Textarea
                id={`org-description-${org.id}`}
                aria-label="Organization description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this organization for?"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={name.trim().length < 2}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
